/* Itens estilo Mario Kart: caixas na pista -> 1 item na mão -> usar (tecla E / B / botão ⚡ / tocar no slot).
 *   🚀 Turbo     impulso sustentado
 *   🍌 Banana    fica na pista; quem passa roda
 *   🟢 Casco verde  anda reto, quica nas bordas
 *   🔴 Casco vermelho  persegue quem está na sua frente (segue a pista)
 * Rede: eventos 'spawn'/'remove' (KartNet.sendEvent). Quem é ATINGIDO decide o acerto (no próprio
 * navegador) e avisa; todos removem o item. O giro do kart atingido já viaja na posição normal. */
import * as THREE from 'three'
import * as CANNON from 'cannon-es'
import { TRACK, TAU, wrapAngle } from '../Race/Track.js'

// ---- ajuste fino ----
const BOX_COUNT_GATES = [0.9, 2.0, 3.1, 4.2, 5.3] // rad à frente da linha
const BOX_LANES = [45.5, 50, 54.5]
const BOX_RESPAWN_MS = 5000
const ROLL_MS = 1100
const BOOST_MS = 1600
const BOOST_IMPULSE = 1.4 // por frame, enquanto dura o turbo
const BOOST_KICK = 55
const SHELL_SPEED = { green: 38, red: 42 }
const SHELL_TTL = { green: 8000, red: 10000 }
const BANANA_TTL = 45000
const STUN_MS = 1600
const INVULN_MS = 900

const KINDS = {
    turbo: { emoji: '🚀', label: 'Turbo' },
    banana: { emoji: '🍌', label: 'Banana' },
    green: { emoji: '🟢', label: 'Casco verde' },
    red: { emoji: '🔴', label: 'Casco vermelho' },
}

function emojiTexture(ch, px = 128) {
    const c = document.createElement('canvas')
    c.width = c.height = px
    const ctx = c.getContext('2d')
    ctx.font = `${px * 0.78}px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif`
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillText(ch, px / 2, px / 2 + px * 0.04)
    return new THREE.CanvasTexture(c)
}
function emojiSprite(ch, scale) {
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: emojiTexture(ch), transparent: true }))
    s.scale.set(scale, scale, 1)
    return s
}

export default class Items {
    constructor(experience) {
        this.exp = experience
        this.scene = experience.scene
        this.held = null
        this.rolling = false
        this.boxes = []
        this.hazards = new Map()
        this._removed = new Set() // ids removidos (se o 'remove' chegar antes do 'spawn')
        this.stunUntil = 0
        this.invulnUntil = 0
        this.boostUntil = 0
        this._n = 0
        this._wasStunned = false
        this._pops = []
        this._tmp = new CANNON.Vec3()

        this._buildBoxes()
        this._buildHUD()
        this._hookKeys()
    }

    get race() { return this.exp.race }
    get car() { return this.exp.simpleCar }
    get net() { return this.exp.kartNet }
    get stunned() { return performance.now() < this.stunUntil }
    get active() { return this.race?.state !== 'countdown' }
    now() { return performance.now() }
    _beep(f, d) { try { this.exp.world?.gameManager?.playSound(f, d) } catch (_) { /* sem áudio */ } }

    // ---------- caixas ----------
    _buildBoxes() {
        const c = document.createElement('canvas')
        c.width = c.height = 128
        const ctx = c.getContext('2d')
        const g = ctx.createLinearGradient(0, 0, 128, 128)
        g.addColorStop(0, '#ff5a5a'); g.addColorStop(0.33, '#ffd84a'); g.addColorStop(0.66, '#4aff8a'); g.addColorStop(1, '#4aa8ff')
        ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128)
        ctx.fillStyle = '#fff'; ctx.font = 'bold 96px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
        ctx.strokeStyle = 'rgba(0,0,0,.5)'; ctx.lineWidth = 8
        ctx.strokeText('?', 64, 70); ctx.fillText('?', 64, 70)
        const mat = new THREE.MeshToonMaterial({ map: new THREE.CanvasTexture(c), transparent: true, opacity: 0.92 })
        const geo = new THREE.BoxGeometry(1.5, 1.5, 1.5)
        for (const a of BOX_COUNT_GATES) {
            for (const R of BOX_LANES) {
                const th = TRACK.startAngle + a
                const m = new THREE.Mesh(geo, mat)
                m.position.set(R * Math.cos(th), 1.5, R * Math.sin(th))
                this.scene.add(m)
                this.boxes.push({ mesh: m, back: 0 })
            }
        }
    }

    // ---------- HUD ----------
    _buildHUD() {
        const el = document.createElement('div')
        el.id = 'item-slot'
        el.style.cssText = 'position:fixed;top:56px;left:10px;z-index:56;width:68px;height:68px;border-radius:16px;background:rgba(0,0,0,.55);border:2px solid rgba(255,255,255,.35);display:flex;flex-direction:column;align-items:center;justify-content:center;font:36px system-ui;color:#fff;cursor:pointer;user-select:none;-webkit-user-select:none;touch-action:manipulation'
        el.innerHTML = '<span id="item-emoji" style="line-height:1"></span><span id="item-label" style="font:600 10px system-ui;opacity:.8;margin-top:2px"></span>'
        el.addEventListener('pointerdown', (e) => { e.preventDefault(); this.useItem() })
        document.body.appendChild(el)
        this._slot = el
        this._emojiEl = el.querySelector('#item-emoji')
        this._labelEl = el.querySelector('#item-label')
        this._drawSlot()
    }
    _drawSlot(emoji, label) {
        const k = this.held && KINDS[this.held]
        this._emojiEl.textContent = emoji ?? (k ? k.emoji : '')
        this._labelEl.textContent = label ?? (k ? k.label : 'item')
        this._slot.style.borderColor = k ? '#ffe14a' : 'rgba(255,255,255,.35)'
        this._slot.style.opacity = k || emoji ? '1' : '0.6'
    }

    _hookKeys() {
        // (as teclas B/E chamam useItem pelo SimpleCarController; aqui só o 'E' direto, caso o carro esteja travado)
    }

    // ---------- pegar / sortear ----------
    _startRoll() {
        this.rolling = true
        const pool = ['🚀', '🍌', '🟢', '🔴']
        let i = 0
        const iv = setInterval(() => this._drawSlot(pool[i++ % pool.length], 'sorteando…'), 90)
        this._beep(660, 0.08)
        setTimeout(() => {
            clearInterval(iv)
            this.held = this._pick()
            this.rolling = false
            this._drawSlot()
            this._beep(880, 0.12)
        }, ROLL_MS)
    }

    _pick() {
        const st = this.race?.standings?.() || []
        const n = st.length
        const rank = Math.max(0, st.findIndex((s) => s.me))
        const f = n > 1 ? rank / (n - 1) : 0.5 // 0 = primeiro, 1 = último
        const mix = (a, b) => a * (1 - f) + b * f
        const w = { banana: mix(40, 15), green: mix(35, 15), turbo: mix(20, 35), red: mix(5, 35) }
        let r = Math.random() * Object.values(w).reduce((a, b) => a + b, 0)
        for (const [k, v] of Object.entries(w)) { if ((r -= v) <= 0) return k }
        return 'turbo'
    }

    // ---------- usar ----------
    useItem() {
        if (!this.held || this.rolling || !this.active || this.stunned) return
        const car = this.car
        if (!car?.vehicle?.chassisBody) return
        const kind = this.held
        this.held = null
        this._drawSlot()
        const body = car.vehicle.chassisBody
        const f = car.getForwardDirection()
        const fx = f.x, fz = f.z

        if (kind === 'turbo') {
            this.boostUntil = this.now() + BOOST_MS
            body.applyImpulse(new CANNON.Vec3(fx * BOOST_KICK, 0, fz * BOOST_KICK), new CANNON.Vec3())
            car.experience.world?.simpleCarController?.createBoostEffect?.(car.chassisMesh.position)
            this._beep(500, 0.1); setTimeout(() => this._beep(800, 0.15), 80)
            return
        }

        const p = body.position
        const id = `${this.race.myId()}:${++this._n}`
        let h
        if (kind === 'banana') {
            h = { id, kind, owner: this.race.myId(), x: p.x - fx * 3.4, z: p.z - fz * 3.4, dx: 0, dz: 0 }
            this._beep(300, 0.12)
        } else {
            let target = null
            if (kind === 'red') {
                const st = this.race.standings()
                const i = st.findIndex((s) => s.me)
                if (i > 0 && st[i - 1].fin == null) target = st[i - 1].id
            }
            h = { id, kind, owner: this.race.myId(), x: p.x + fx * 3.6, z: p.z + fz * 3.6, dx: fx, dz: fz, target }
            this._beep(kind === 'red' ? 700 : 560, 0.15)
        }
        this._addHazard(h)
        this.net?.sendEvent({ ev: 'spawn', ...h })
    }

    // ---------- perigos (banana / cascos) ----------
    _addHazard(h) {
        if (this.hazards.has(h.id)) return
        const born = this.now()
        let mesh
        if (h.kind === 'banana') {
            mesh = emojiSprite('🍌', 2.2)
            mesh.position.set(h.x, 1.0, h.z)
        } else {
            mesh = new THREE.Group()
            const col = h.kind === 'red' ? 0xe53935 : 0x2ecc40
            const shell = new THREE.Mesh(new THREE.SphereGeometry(0.7, 16, 12), new THREE.MeshToonMaterial({ color: col }))
            const ring = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.13, 8, 20), new THREE.MeshToonMaterial({ color: 0xffffff }))
            ring.rotation.x = Math.PI / 2
            shell.castShadow = true
            mesh.add(shell, ring)
            mesh.position.set(h.x, 0.8, h.z)
        }
        this.scene.add(mesh)
        const hz = {
            ...h, mesh, born,
            ttl: h.kind === 'banana' ? BANANA_TTL : SHELL_TTL[h.kind],
            speed: SHELL_SPEED[h.kind] || 0,
        }
        this.hazards.set(h.id, hz)
    }

    _removeHazard(id, hit) {
        const h = this.hazards.get(id)
        if (!h) return
        this.scene.remove(h.mesh)
        h.mesh.traverse?.((o) => { o.geometry?.dispose?.(); o.material?.dispose?.() })
        h.mesh.material?.map?.dispose?.()
        h.mesh.material?.dispose?.()
        this.hazards.delete(id)
        if (hit) this._pop(h.x, h.z)
    }

    _pop(x, z) {
        const s = emojiSprite('💥', 1)
        s.position.set(x, 1.4, z)
        this.scene.add(s)
        this._pops.push({ s, t: 0 })
    }

    _targetPos(id) {
        if (!id) return null
        if (id === this.race.myId()) { const p = this.car?.vehicle?.chassisBody?.position; return p ? { x: p.x, z: p.z } : null }
        const r = this.net?.remotes?.get(id)
        return r ? { x: r.curP.x, z: r.curP.z } : null
    }

    _stepHazard(h, dt) {
        if (h.kind === 'green') {
            h.x += h.dx * h.speed * dt
            h.z += h.dz * h.speed * dt
            // quica nas bordas da pista
            const r = Math.hypot(h.x, h.z)
            if (r > 58.5 || r < 41.5) {
                const out = r > 58.5
                const nx = h.x / r, nz = h.z / r
                const lim = out ? 58.5 : 41.5
                h.x = nx * lim; h.z = nz * lim
                const s = out ? 1 : -1 // normal que aponta para fora da pista
                const dn = (h.dx * nx + h.dz * nz) * s
                if (dn > 0) { h.dx -= 2 * dn * nx * s; h.dz -= 2 * dn * nz * s }
            }
            h.mesh.rotation.y += dt * 12
        } else if (h.kind === 'red') {
            let th = Math.atan2(h.z, h.x)
            let rr = Math.hypot(h.x, h.z)
            const tp = this._targetPos(h.target)
            const adv = (h.speed / Math.max(rr, 20)) * dt
            if (tp) {
                const dth = wrapAngle(Math.atan2(tp.z, tp.x) - th)
                const trad = Math.hypot(tp.x, tp.z)
                rr += (trad - rr) * Math.min(1, 3 * dt)
                th += dth > -0.25 && dth < adv ? dth : adv
            } else {
                th += adv
            }
            rr = Math.min(58.5, Math.max(41.5, rr))
            h.x = rr * Math.cos(th); h.z = rr * Math.sin(th)
            h.mesh.rotation.y += dt * 12
        }
        h.mesh.position.x = h.x
        h.mesh.position.z = h.z
    }

    // ---------- loop ----------
    update() {
        const dt = Math.min(this.exp.time.delta, 100) / 1000
        const t = this.now()
        const car = this.car
        const body = car?.vehicle?.chassisBody
        if (!body) return

        // caixas
        for (const b of this.boxes) {
            b.mesh.rotation.y += dt * 1.8
            b.mesh.position.y = 1.5 + Math.sin(t / 400 + b.mesh.position.x) * 0.15
            if (b.back && t >= b.back) { b.back = 0; b.mesh.visible = true }
            if (b.mesh.visible && this.active && !this.held && !this.rolling) {
                const dx = body.position.x - b.mesh.position.x
                const dz = body.position.z - b.mesh.position.z
                if (dx * dx + dz * dz < 2.6 * 2.6 && Math.abs(body.position.y - 1.5) < 3) {
                    b.mesh.visible = false
                    b.back = t + BOX_RESPAWN_MS
                    this._startRoll()
                }
            }
        }

        // perigos
        for (const [id, h] of [...this.hazards]) {
            if (t - h.born > h.ttl) { this._removeHazard(id, false); continue }
            this._stepHazard(h, dt)
            if (!this.active || t < this.invulnUntil) continue
            const own = h.owner === this.race.myId() && t - h.born < (h.kind === 'banana' ? 1200 : 900)
            if (own) continue
            const dx = body.position.x - h.x, dz = body.position.z - h.z
            const rad = h.kind === 'banana' ? 1.9 : 2.1
            if (dx * dx + dz * dz < rad * rad && Math.abs(body.position.y - h.mesh.position.y) < 2.5) {
                this._removeHazard(id, true)
                this.net?.sendEvent({ ev: 'remove', id, hit: true })
                this._stun()
            }
        }

        // turbo sustentado
        if (t < this.boostUntil && !this.stunned) {
            const f = car.getForwardDirection()
            this._tmp.set(f.x * BOOST_IMPULSE, 0, f.z * BOOST_IMPULSE)
            body.applyImpulse(this._tmp, new CANNON.Vec3())
        }

        // atordoado: sem controle; ao acabar, reaplica o que o jogador está segurando
        const st = this.stunned
        if (st) this.race?._releaseControls?.()
        if (this._wasStunned && !st) this.exp.touchControls?.apply?.()
        this._wasStunned = st

        // explosões
        for (let i = this._pops.length - 1; i >= 0; i--) {
            const p = this._pops[i]
            p.t += dt
            const k = p.t / 0.45
            p.s.scale.setScalar(1 + k * 3)
            p.s.material.opacity = Math.max(0, 1 - k)
            if (k >= 1) { this.scene.remove(p.s); p.s.material.map?.dispose(); p.s.material.dispose(); this._pops.splice(i, 1) }
        }
    }

    _stun() {
        const t = this.now()
        const body = this.car.vehicle.chassisBody
        this.stunUntil = t + STUN_MS
        this.invulnUntil = t + STUN_MS + INVULN_MS
        this.boostUntil = 0
        body.velocity.scale(0.3, body.velocity)
        body.velocity.y = 5
        body.angularVelocity.y = (Math.random() < 0.5 ? -1 : 1) * 16
        this.race?._releaseControls?.()
        this._beep(300, 0.15); setTimeout(() => this._beep(220, 0.2), 120); setTimeout(() => this._beep(160, 0.3), 260)
    }

    // ---------- rede ----------
    onNetEvent(m) {
        if (m.ev === 'spawn') {
            if (m.owner === this.race.myId() || this.hazards.has(m.id) || this._removed.has(m.id)) return
            this._addHazard({ id: m.id, kind: m.kind, owner: m.owner, x: m.x, z: m.z, dx: m.dx, dz: m.dz, target: m.target })
        } else if (m.ev === 'remove') {
            this._removed.add(m.id)
            if (this._removed.size > 200) this._removed.delete(this._removed.values().next().value)
            this._removeHazard(m.id, !!m.hit)
        }
    }

    // zera tudo (nova corrida / voltar ao lobby)
    reset() {
        for (const id of [...this.hazards.keys()]) this._removeHazard(id, false)
        this.held = null
        this.rolling = false
        this.boostUntil = 0
        this.stunUntil = 0
        this.invulnUntil = 0
        for (const b of this.boxes) { b.mesh.visible = true; b.back = 0 }
        this._drawSlot()
    }
}
